import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import AutomaticNotifications from '../components/AutomaticNotifications';
import PushDeliveryReport from '../components/PushDeliveryReport';
import { Button, Card, Input, Skeleton, useToast } from '../../design-system';
import '../styles/Broadcast.css';

// Telling the firm something.
//
// The notification stack was already built — rows, unread counts, the bell, the
// sidebar dots, live sockets and Web Push. Every event flowing through it was
// something the SYSTEM observed: a task assigned, a notice due, a payment
// declared. This is the one place a PERSON writes the words, which is what
// makes it a screen rather than another entry in utils/notify.js.
//
// ⚠️ THE AUDIENCE PICKED HERE IS A REQUEST, NOT AN AUTHORISATION. The server
// re-derives every recipient from the database (see
// notificationController.resolveAudience) — a stale user id, a deactivated
// account or a hand-edited request resolves to nobody rather than to a
// notification row somebody should not have. The counts below are this screen
// telling the user what it expects, not what it decides.

const MAX_TITLE = 160;
const MAX_BODY = 2000;

function OrgAdminBroadcast() {
  const toast = useToast();
  const [audience, setAudience] = useState('all');
  const [roleIds, setRoleIds] = useState(new Set());
  const [userIds, setUserIds] = useState(new Set());
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pushSafe, setPushSafe] = useState(false);
  const [q, setQ] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  useEffect(() => {
    apiClient.get('/api/notifications/broadcast/audience')
      .then((r) => setData(r.data))
      .catch((e) => toast.danger(e.response?.data?.error || 'Could not load the firm\'s staff'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const users = data?.users || [];
  const roles = data?.roles || [];
  const selfId = data?.selfId;

  // Who this actually reaches. The sender is always excluded — nobody needs
  // telling what they just wrote — so the count says so rather than promising
  // one more person than will get it.
  const recipients = useMemo(() => {
    if (audience === 'all') return users.filter((u) => u.id !== selfId);
    if (audience === 'role') return users.filter((u) => u.roleId && roleIds.has(u.roleId) && u.id !== selfId);
    return users.filter((u) => userIds.has(u.id) && u.id !== selfId);
  }, [audience, users, roleIds, userIds, selfId]);

  const shownUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => `${u.name} ${u.email} ${u.roleName}`.toLowerCase().includes(needle));
  }, [users, q]);

  const toggle = (set, setter) => (id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };
  const toggleRole = toggle(roleIds, setRoleIds);
  const toggleUser = toggle(userIds, setUserIds);

  const send = async () => {
    if (!title.trim()) { toast.danger('Write a message'); return; }
    if (!recipients.length) { toast.danger('That reaches nobody'); return; }
    // Sending cannot be unsent: there is no recall, and the rows are already in
    // other people's bells (and possibly on their lock screens) the moment this
    // resolves. Worth one confirmation, which is more than the rest of this
    // section asks for and less than an irreversible firm-wide message deserves.
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Send this to ${recipients.length} ${recipients.length === 1 ? 'person' : 'people'}? It cannot be unsent.`)) return;

    setSending(true);
    try {
      const r = await apiClient.post('/api/notifications/broadcast', {
        audience,
        roleIds: [...roleIds],
        userIds: [...userIds],
        title: title.trim(),
        body: body.trim(),
        pushSafe,
      });
      toast.success(`Sent to ${r.data.sentTo} ${r.data.sentTo === 1 ? 'person' : 'people'}`);
      setLastSent({ at: new Date(), count: r.data.sentTo, title: title.trim() });
      setTitle('');
      setBody('');
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  return (
    <OrgAdminLayout>
      <div className="bc-head">
        <h2 className="bc-title">Send Notifications</h2>
        <p className="bc-sub">
          An announcement to the firm&apos;s staff. It lands in their notification bell straight away, and
          on any device where they have turned on desktop notifications. It is not emailed — the bell is
          where internal announcements belong, and mailing them is the quickest way to teach people to
          filter out the deadline notices that arrive from the same place.
        </p>
      </div>

      {loading ? <Skeleton.Text lines={8} /> : (
        <div className="bc-grid">
          {/* ⚠️ A COLUMN, NOT TWO GRID CELLS. These two were children of the
              grid itself, which put "Sent automatically" in row 2 — and a grid
              row starts below the TALLEST item of the row above it, so the
              card sat level with the bottom of the message composer with a
              screen of white space above it. Stacking them makes the second
              card follow the first: it fills the gap under a short audience
              picker, and is pushed down by the long one that "Particular
              people" opens. */}
          <div className="bc-col">
            {/* ── Who ── */}
            <Card className="bc-audience">
              <h3 className="bc-h">Who gets it</h3>

              <label className="bc-radio">
                <input type="radio" name="aud" checked={audience === 'all'} onChange={() => setAudience('all')} />
                <span>
                  <b>Everyone at the firm</b>
                  <small>{users.length - (selfId ? 1 : 0)} people, excluding you</small>
                </span>
              </label>

              <label className="bc-radio">
                <input type="radio" name="aud" checked={audience === 'role'} onChange={() => setAudience('role')} />
                <span>
                  <b>By role</b>
                  <small>Everyone holding one of the roles you pick</small>
                </span>
              </label>
              {audience === 'role' && (
                <div className="bc-choices">
                  {roles.map((r) => (
                    <label key={r.id} className="bc-check">
                      <input type="checkbox" checked={roleIds.has(r.id)} onChange={() => toggleRole(r.id)} />
                      <span>{r.name}<span className="bc-muted"> · {r.count}</span></span>
                    </label>
                  ))}
                  {roles.length === 0 && <p className="bc-empty">No roles found.</p>}
                </div>
              )}

              <label className="bc-radio">
                <input type="radio" name="aud" checked={audience === 'users'} onChange={() => setAudience('users')} />
                <span>
                  <b>Particular people</b>
                  <small>Pick them one by one</small>
                </span>
              </label>
              {audience === 'users' && (
                <div className="bc-choices">
                  <Input placeholder="Search staff…" value={q} onChange={(e) => setQ(e.target.value)} />
                  <div className="bc-people">
                    {shownUsers.map((u) => (
                      <label key={u.id} className={`bc-check ${u.id === selfId ? 'bc-is-self' : ''}`}>
                        <input
                          type="checkbox"
                          checked={userIds.has(u.id)}
                          onChange={() => toggleUser(u.id)}
                          disabled={u.id === selfId}
                        />
                        <span>
                          {u.name}
                          <span className="bc-muted">
                            {' · '}{u.roleName || 'No role'}{u.id === selfId ? ' · you' : ''}
                          </span>
                        </span>
                      </label>
                    ))}
                    {shownUsers.length === 0 && <p className="bc-empty">Nobody matches that.</p>}
                  </div>
                </div>
              )}
            </Card>

            {/* Directly under the picker, so the person who opened this page
                looking for the DSC reminder's clock finds it without
                scrolling past the composer. */}
            <AutomaticNotifications />

            {/* The other half of "notifications": not what we send, but what
                actually landed. It renders nothing for anybody below Manager,
                and nothing at all until something has been pushed. */}
            <PushDeliveryReport />
          </div>

          {/* ── What ── */}
          <Card className="bc-message">
            <h3 className="bc-h">The message</h3>

            <Input
              label="Message"
              placeholder="e.g. The office is closed on Friday for Diwali."
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
              helperText={`${title.length} / ${MAX_TITLE} — this is the line people see in the bell`}
            />

            <div className="ds-field">
              <label htmlFor="bc-body" className="ds-field-label">More detail (optional)</label>
              <textarea
                id="bc-body"
                className="ds-input"
                rows="4"
                placeholder="Anything that does not fit in one line."
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
              />
              <span className="ds-field-helper">{body.length} / {MAX_BODY}</span>
            </div>

            {/* ⚠️ Off by default, and the copy explains the consequence rather
                than the mechanism. A desktop notification renders on a lock
                screen, outside the session that authenticated the reader — the
                same reason chat bodies and client names never appear in one. */}
            <label className="bc-check bc-check-block">
              <input type="checkbox" checked={pushSafe} onChange={(e) => setPushSafe(e.target.checked)} />
              <span>
                <b>Show the words on desktop and phone notifications</b>
                <small>
                  Off by default. With it off people still get a notification, but it reads
                  &ldquo;New notification&rdquo; until they open the app. Turn it on only if the message
                  is fine to appear on a locked screen someone else might be standing next to.
                </small>
              </span>
            </label>

            <div className="bc-preview">
              <div className="bc-preview-label">In the bell</div>
              <div className="bc-preview-row">
                <div className="bc-preview-dot" />
                <div>
                  <div className="bc-preview-title">{title.trim() || 'Your message'}</div>
                  {body.trim() && <div className="bc-preview-body">{body.trim()}</div>}
                  <div className="bc-preview-meta">Announcement · just now</div>
                </div>
              </div>
            </div>

            <div className="bc-send">
              <span className="bc-count">
                {recipients.length === 0
                  ? 'Nobody selected'
                  : `Goes to ${recipients.length} ${recipients.length === 1 ? 'person' : 'people'}`}
              </span>
              <Button
                variant="primary"
                onClick={send}
                loading={sending}
                disabled={!title.trim() || recipients.length === 0}
              >
                Send
              </Button>
            </div>

            {lastSent && (
              <p className="bc-last">
                Last sent {lastSent.at.toLocaleTimeString('en-IN')} to {lastSent.count}
                {lastSent.count === 1 ? ' person' : ' people'}: &ldquo;{lastSent.title}&rdquo;
              </p>
            )}
          </Card>
        </div>
      )}
    </OrgAdminLayout>
  );
}

export default OrgAdminBroadcast;
